import React from 'react';
import {
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Alert,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import AuthorsSidebar from '../../components/AuthorsSidebar';
import {
  Copyright,
  Image,
  Link,
  Warning,
  CheckCircle,
  Info,
} from '@mui/icons-material';

const BorrowedMaterials: React.FC = () => {
  const citationRules = [
    'Все источники должны быть правильно оформлены',
    'Цитаты должны быть заключены в кавычки',
    'Обязательно указание страниц для прямых цитат',
    'Использование актуальных и авторитетных источников',
  ];

  const imageUsage = [
    {
      title: 'Собственные изображения',
      description: 'Фотографии, схемы, диаграммы, созданные автором',
      requirements: 'Не требуют дополнительных разрешений',
    },
    {
      title: 'Заимствованные изображения',
      description: 'Изображения из других источников',
      requirements: 'Требуют письменного разрешения правообладателя',
    },
    {
      title: 'Изображения с открытой лицензией',
      description: 'Creative Commons, Public Domain',
      requirements: 'Соблюдение условий лицензии',
    },
  ];

  const copyrightAspects = [
    'Авторские права на текст статьи',
    'Права на иллюстрации и изображения',
    'Права на таблицы и графики',
    'Права на переводы и адаптации',
  ];

  const permissions = [
    'Письменное разрешение от правообладателя',
    'Указание источника заимствования',
    'Соблюдение условий использования',
    'Оформление в соответствии с требованиями',
  ];

  return (
    <AuthorsSidebar>
      <Container sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Использование заимствованных материалов
      </Typography>
      
      <Typography variant="body1" paragraph>
        При подготовке статьи авторы должны соблюдать авторские права и правильно 
        оформлять все заимствованные материалы.
      </Typography>

      <Alert severity="warning" sx={{ mb: 4 }}>
        Нарушение авторских прав может привести к отказу в публикации или правовым последствиям.
      </Alert>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Link />
          Цитирование источников
        </Typography>
        <Typography variant="body1" paragraph>
          Правильное цитирование является основой научной этики и обязательным требованием 
          для всех публикаций.
        </Typography>
        <List>
          {citationRules.map((rule, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText primary={rule} />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Image />
          Использование изображений
        </Typography>
        <Typography variant="body1" paragraph>
          При использовании изображений необходимо учитывать их правовой статус 
          и получать соответствующие разрешения.
        </Typography>
        
        <Grid container spacing={2}>
          {imageUsage.map((type, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {type.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {type.description}
                  </Typography>
                  <Box sx={{ mt: 2, p: 1, backgroundColor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Требования:</strong> {type.requirements}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Copyright />
          Авторские права
        </Typography>
        <Typography variant="body1" paragraph>
          Авторы должны понимать различные аспекты авторского права при подготовке статей.
        </Typography>
        <List>
          {copyrightAspects.map((aspect, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <Info color="info" />
              </ListItemIcon>
              <ListItemText primary={aspect} />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Разрешения на использование
        </Typography>
        <Typography variant="body1" paragraph>
          Для использования заимствованных материалов необходимо получить соответствующие разрешения.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          Что требуется для получения разрешений:
        </Typography>
        <List>
          {permissions.map((permission, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText primary={permission} />
            </ListItem>
          ))}
        </List>

        <Box sx={{ mt: 3, p: 2, backgroundColor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="info.contrastText">
            <strong>Рекомендация:</strong> Сохраняйте все документы о полученных разрешениях 
            и прикладывайте их к статье при подаче.
          </Typography>
        </Box>
      </Paper>
      </Container>
    </AuthorsSidebar>
  );
};

export default BorrowedMaterials;
